import { useQuery } from '@tanstack/react-query';
import {
    getProviderHealth,
    getOperationsDashboard,
    getOperationsQueue,
    getOperationDetail,
    getDlqEntries,
    getDlqEntry,
    getCompensationJobs,
    getCompensationJob,
    getDegradationStatus,
    getMyOperations,
} from '../api';
import { queryKeys } from '../../../api/queryKeys';

export const useOperationsDashboard = () => {
    const query = useQuery({
        queryKey: ['operations', 'dashboard'],
        queryFn: async () => {
            const data = await getOperationsDashboard();
            return {
                health: data?.health ?? [],
                queue: data?.queue ?? { total: 0, statusCounts: {}, recent: [] },
                dlq: data?.dlq ?? { total: 0, statusCounts: {}, recent: [] },
                compensation: data?.compensation ?? { total: 0, statusCounts: {}, recent: [] },
                summarizedAt: data?.summarizedAt ?? null,
            };
        },
        refetchInterval: 15_000,
    });

    return {
        dashboard: query.data ?? { health: [], queue: { total: 0, recent: [] }, dlq: { total: 0, recent: [] }, compensation: { total: 0, recent: [] }, summarizedAt: null },
        loading: query.isLoading,
        error: query.isError ? 'خطا در دریافت داشبورد عملیات' : null,
        refresh: query.refetch,
    };
};

export const useProviderHealth = (refetchMs = 15_000) => {
    const query = useQuery({
        queryKey: ['operations', 'health'],
        queryFn: async () => (await getProviderHealth()) ?? [],
        refetchInterval: refetchMs,
    });
    return {
        providers: query.data ?? [],
        loading: query.isLoading,
        error: query.isError ? 'خطا در دریافت وضعیت سرویس‌ها' : null,
        refresh: query.refetch,
    };
};

export const useOperationsQueue = (params = {}) => {
    const query = useQuery({
        queryKey: ['operations', 'queue', params],
        queryFn: async () => {
            const data = await getOperationsQueue({ limit: 50, offset: 0, ...params });
            return { operations: data?.operations ?? [], total: data?.total ?? 0 };
        },
    });
    return {
        operations: query.data?.operations ?? [],
        total: query.data?.total ?? 0,
        loading: query.isLoading,
        error: query.isError ? 'خطا در دریافت صف عملیات' : null,
        refresh: query.refetch,
    };
};

export const useOperationDetail = (id) => {
    const query = useQuery({
        queryKey: ['operations', 'queue', id],
        queryFn: async () => getOperationDetail(id),
        enabled: Boolean(id),
    });
    return {
        operation: query.data ?? null,
        loading: query.isLoading,
        error: query.isError ? 'خطا در دریافت جزئیات عملیات' : null,
        refresh: query.refetch,
    };
};

export const useDlq = (params = {}) => {
    const query = useQuery({
        queryKey: ['operations', 'dlq', params],
        queryFn: async () => {
            const data = await getDlqEntries({ limit: 50, offset: 0, ...params });
            return { entries: data?.entries ?? [], total: data?.total ?? 0 };
        },
    });
    return {
        entries: query.data?.entries ?? [],
        total: query.data?.total ?? 0,
        loading: query.isLoading,
        error: query.isError ? 'خطا در دریافت صف مرگ‌شده' : null,
        refresh: query.refetch,
    };
};

export const useDlqEntry = (id) => {
    const query = useQuery({
        queryKey: ['operations', 'dlq', id],
        queryFn: async () => getDlqEntry(id),
        enabled: Boolean(id),
    });
    return {
        entry: query.data ?? null,
        loading: query.isLoading,
        error: query.isError ? 'خطا در دریافت جزئیات' : null,
        refresh: query.refetch,
    };
};

export const useCompensationJobs = (params = {}) => {
    const query = useQuery({
        queryKey: ['operations', 'compensation', params],
        queryFn: async () => {
            const data = await getCompensationJobs({ limit: 50, offset: 0, ...params });
            return { jobs: data?.jobs ?? [], total: data?.total ?? 0 };
        },
    });
    return {
        jobs: query.data?.jobs ?? [],
        total: query.data?.total ?? 0,
        loading: query.isLoading,
        error: query.isError ? 'خطا در دریافت کارهای جبرانی' : null,
        refresh: query.refetch,
    };
};

export const useCompensationJob = (id) => {
    const query = useQuery({
        queryKey: ['operations', 'compensation', id],
        queryFn: async () => getCompensationJob(id),
        enabled: Boolean(id),
    });
    return {
        job: query.data ?? null,
        loading: query.isLoading,
        error: query.isError ? 'خطا در دریافت جزئیات' : null,
        refresh: query.refetch,
    };
};

export const useDegradationStatus = (refetchMs = 30_000) => {
    const query = useQuery({
        queryKey: ['operations', 'degradation'],
        queryFn: async () => (await getDegradationStatus()) ?? { degraded: false, issuanceBlocked: false, providers: [] },
        refetchInterval: refetchMs,
    });
    return {
        degradation: query.data ?? { degraded: false, issuanceBlocked: false, providers: [] },
        loading: query.isLoading,
        refresh: query.refetch,
    };
};

export const useMyOperations = () => {
    const query = useQuery({
        queryKey: ['operations', 'mine'],
        queryFn: async () => (await getMyOperations()) ?? [],
    });
    return {
        operations: query.data ?? [],
        loading: query.isLoading,
        error: query.isError ? 'خطا در دریافت عملیات' : null,
        refresh: query.refetch,
    };
};